import React, { forwardRef } from 'react';
import { Box, Button, ButtonProps, IconButton } from '@landr/maestro';
import { css } from '@landr/styled-system/css';
import { noop } from 'utils/noop';
import {
    usePrivateActionClickHandler,
    PrivateActionClickHandlerParams,
} from 'hooks/usePrivateActionClickHandler/usePrivateActionClickHandler';
import { AnyAuthenticatedUserAction } from 'types';

const wrapperStyles = css({
    _focusVisible: {
        outline: 'none',
    },
});

export interface ActionButtonProps
    extends Omit<ButtonProps, 'onClick'>,
        Omit<PrivateActionClickHandlerParams, 'userAction'> {
    label: string;
    userAction?: AnyAuthenticatedUserAction;
    tabIndex?: number;
    children?: React.ReactNode;
}

export const ActionButton = forwardRef(
    (
        {
            authOnly = false,
            onClick = noop,
            preventDefault = true,
            tabIndex,
            stopPropagation = true,
            analyticsCallback = noop,
            isLoading,
            children,
            label,
            userAction = { type: '', description: '', args: [] },
            ...props
        }: ActionButtonProps,
        ref: React.Ref<HTMLDivElement>,
    ) => {
        const handleClick = usePrivateActionClickHandler({
            authOnly,
            onClick,
            preventDefault,
            stopPropagation,
            analyticsCallback,
            isLoading,
            userAction,
        });

        return children ? (
            <Box className={wrapperStyles} tabIndex={-1} ref={ref}>
                <Button
                    isLoading={isLoading}
                    tabIndex={tabIndex}
                    onClick={handleClick}
                    {...props}
                >
                    {children}
                </Button>
            </Box>
        ) : (
            <Box className={wrapperStyles} tabIndex={-1} ref={ref}>
                <IconButton
                    isLoading={isLoading}
                    tabIndex={tabIndex}
                    onClick={handleClick}
                    label={label}
                    {...props}
                />
            </Box>
        );
    },
);
